<template>
  <div>
    <template v-if="notFound">
      <div class="alert alert-warning" role="alert" style="margin-top: 30px">
        No account found for <strong>{{ $route.params.email }}</strong
        >.
        <router-link :to="{ name: 'accounts-browse' }"
          >Back to accounts</router-link
        >
      </div>
    </template>
    <template v-else-if="!account">
      <div class="alert alert-info" role="alert" style="margin-top: 30px">
        <em>Loading the account's details... Please wait.</em>
      </div>
    </template>
    <template v-else>
      <div
        class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3"
      >
        <h1 class="h2">
          <span class="clickable-email" @click="copyEmailToClipboard">
            {{ account.email }}
            <span class="copy-indicator">{{ copyText }}</span>
          </span>
          <span v-if="account.locked">&#x1f512;</span>
          <router-link
            v-if="isHardLimited"
            :to="{
              name: 'hard-limited-accounts',
              query: { search: account.email },
            }"
            class="badge badge-danger"
            style="font-size: 0.6em; vertical-align: middle; margin-left: 0.4em"
            title="This account is hard-limited"
            >hard-limited</router-link
          >
          <span
            v-if="account.is_removed"
            class="badge badge-danger"
            style="font-size: 0.5em; vertical-align: middle"
            >removed</span
          >
          <template v-if="account.certified">
            <svg viewBox="0 0 24 24" aria-label="Verified account">
              <g>
                <path
                  d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"
                ></path>
              </g>
            </svg>
          </template>
        </h1>
        <button
          title="Go to plan"
          v-if="account.premium"
          class="badge badge-success"
          style="font-size: 2em"
          @click="goToPlan"
        >
          {{ account.plan.display }}
          <small v-if="account.cancels_on">(set to downgrade)</small>
        </button>
        <span v-else class="badge badge-success" style="font-size: 2em"
          >Free</span
        >
      </div>
      <ul class="nav nav-tabs">
        <li class="nav-item">
          <router-link
            :to="{
              name: 'account-details',
              params: { email: $route.params.email },
            }"
            class="nav-link"
            active-class="active"
            :exact="true"
            >Details</router-link
          >
        </li>
        <li class="nav-item">
          <router-link
            :to="{
              name: 'account-details-domains',
              params: { email: $route.params.email },
            }"
            class="nav-link"
            active-class="active"
            :exact="true"
            >Domains</router-link
          >
        </li>
        <li class="nav-item">
          <router-link
            :to="{
              name: 'account-actions',
              params: { email: $route.params.email },
            }"
            class="nav-link"
            active-class="active"
            :exact="true"
            >Actions</router-link
          >
        </li>
        <li class="nav-item">
          <router-link
            :to="{
              name: 'account-details-invoices',
              params: { email: $route.params.email },
            }"
            class="nav-link"
            active-class="active"
            :exact="true"
            >Invoices</router-link
          >
        </li>
        <li class="nav-item">
          <router-link
            :to="{
              name: 'account-details-subscriptions',
              params: { email: $route.params.email },
            }"
            class="nav-link"
            active-class="active"
            :exact="true"
            >Subscriptions</router-link
          >
        </li>
        <li class="nav-item">
          <router-link
            :to="{
              name: 'account-details-logs',
              params: { email: $route.params.email },
            }"
            class="nav-link"
            active-class="active"
            :exact="true"
            >Activity Log</router-link
          >
        </li>
        <li class="nav-item">
          <router-link
            :to="{
              name: 'account-details-emails',
              params: { email: $route.params.email },
            }"
            class="nav-link"
            active-class="active"
            :exact="true"
            >Emails</router-link
          >
        </li>
        <li class="nav-item">
          <router-link
            :to="{
              name: 'account-details-usage',
              params: { email: $route.params.email },
            }"
            class="nav-link"
            active-class="active"
            :exact="true"
            >Usage</router-link
          >
        </li>
        <li class="nav-item">
          <router-link
            :to="{
              name: 'account-details-plans',
              params: { email: $route.params.email },
            }"
            class="nav-link"
            active-class="active"
            :exact="true"
            >Plans</router-link
          >
        </li>
      </ul>
      <div class="tab-content">
        <div class="tab-pane active show">
          <router-view />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { formatHttpError } from "../../utils/errors";

export default {
  data() {
    return {
      account: null,
      isHardLimited: false,
      notFound: false,
      copyText: "Copy",
    };
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.loadDetails(to.params.email);
    });
  },
  beforeRouteUpdate(to, from, next) {
    next();
    this.loadDetails(to.params.email);
  },
  methods: {
    copyEmailToClipboard() {
      navigator.clipboard.writeText(this.account.email);
      this.copyText = "Copied";
      setTimeout(() => {
        this.copyText = "Copy";
      }, 2000);
    },
    loadDetails(email) {
      this.notFound = false;
      this.$http.get("accounts/" + email).then(
        (response) => {
          if (email !== this.$route.params.email) return;
          this.account = response.body.account;
          this.loadHardLimited(this.account.id);
        },
        (response) => {
          if (email !== this.$route.params.email) return;
          if (response.status === 401) return;
          if (response.status === 404) {
            this.account = null;
            this.notFound = true;
            return;
          }
          alert(formatHttpError(response));
        },
      );
    },
    loadHardLimited(accountId) {
      this.isHardLimited = false;
      this.$http.get("hard-limited-accounts").then(
        (response) => {
          this.isHardLimited = response.body.items.some(
            (item) => item.account_id === accountId,
          );
        },
        (response) => {
          if (response.status === 401) return;
          alert(formatHttpError(response));
        },
      );
    },
    goToPlan() {
      this.$router.push({
        name: "plans-details",
        params: { id: this.account.plan.id },
      });
    },
  },
};
</script>

<style scoped lang="scss">
.clickable-email {
  cursor: pointer;
  position: relative;

  .copy-indicator {
    font-size: 0.5em;
    color: rgb(148, 163, 184);
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover {
    .copy-indicator {
      opacity: 1;
    }
  }
}

h1.h2 svg {
  color: #1d9bf0;
  fill: currentColor;
  height: 24px;
  width: 24px;

  g,
  path {
    color: #1d9bf0;
    fill: currentColor;
  }
}
</style>
